import { useWorkspace } from '@/features/workspaces/hooks';
import { RuleAudienceIcon, RuleEventIcon, RuleTraitIcon, RuleViewIcon } from '@/lib/Icons';
import { Condition } from '@/models/rules';
import { useGetAudiencesQuery } from '@/services/endpoints/audiences';
import { useGetRulesQuery } from '@/services/endpoints/rules';
import { Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

type ConditionSelectorProps = {
  datasourceIds?: string[];
  onConditionSelected: (condition: Condition) => void;
  isFirstCondition?: boolean;
};

export const ConditionSelector: FC<ConditionSelectorProps> = ({ onConditionSelected, isFirstCondition, datasourceIds }) => {
  const { t } = useTranslation('audience_edit');

  const workspace = useWorkspace();

  const { isLoading: isAudiencesLoading, data: audiences } = useGetAudiencesQuery({
    workspaceId: workspace.id,
  });

  const { isLoading: isLoadingView, data: viewRules } = useGetRulesQuery({
    workspaceId: workspace.id,
    type: 'VIEW',
    datasourceIds: datasourceIds || [],
  });

  const { isLoading: isLoadingTrack, data: trackRules } = useGetRulesQuery({
    workspaceId: workspace.id,
    type: 'TRACK',
    datasourceIds: datasourceIds || [],
  });

  const CONDITIONS: { condition: Condition; name: string; icon: any }[] = [
    {
      name: t('partOfAnAudience'),
      icon: RuleAudienceIcon,
      condition: {
        id: 1,
        kind: 'MEMBERSHIP',
      },
    },
    {
      name: t('performedAnEvent'),
      icon: RuleEventIcon,
      condition: {
        id: 2,
        kind: 'EVENT',
      },
    },
    {
      name: t('viewedAPage'),
      icon: RuleViewIcon,
      condition: {
        id: 3,
        kind: 'VISIT',
      },
    },
    {
      name: t('haveAProperty'),
      icon: RuleTraitIcon,
      condition: {
        id: 4,
        kind: 'PROPERTY',
      },
    },
  ];

  return (
    <Popover className='relative max-w-max'>
      {() => (
        <>
          <Popover.Button
            className={`group inline-flex items-center justify-center rounded-lg border-2 bg-gray-100 px-4 py-2 font-medium opacity-50 outline-none hover:bg-gray-50 focus:text-primary-900 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 disabled:opacity-40`}
          >
            {/* <ConditionIcon className={`group-focus:inline-block hidden mr-2 h-5 w-5 text-blue-700 transition ease-in-out duration-150`} aria-hidden='true' /> */}
            <span className='pr-20'>{t(isFirstCondition ? 'rules.buttons.add_first_condition' : 'rules.buttons.add_condition')}</span>
            <ChevronDownIcon className={`ml-2 size-5 text-quaternary-400 transition duration-150 ease-in-out`} aria-hidden='true' />
          </Popover.Button>
          <Transition
            as={Fragment}
            enter='transition ease-out duration-200'
            enterFrom='opacity-0 translate-y-1'
            enterTo='opacity-100 translate-y-0'
            leave='transition ease-in duration-150'
            leaveFrom='opacity-100 translate-y-0'
            leaveTo='opacity-0 translate-y-1'
          >
            <Popover.Panel static className='absolute -left-1 top-full z-10 mt-1 w-full origin-top-right'>
              {({ close }) => (
                <div className='w-full overflow-hidden rounded-lg border shadow-lg'>
                  <div className='relative gap-8 bg-white-100'>
                    {CONDITIONS.map((c) => {
                      let isDisabled = false;
                      let isLoading = false;
                      switch (c.condition.kind) {
                        case 'MEMBERSHIP':
                          isDisabled = audiences?.items.length == 0;
                          isLoading = isAudiencesLoading;
                          break;
                        case 'EVENT':
                          isDisabled = trackRules?.length == 0;
                          isLoading = isLoadingTrack;
                          break;
                        case 'VISIT':
                          isDisabled = viewRules?.length == 0;
                          isLoading = isLoadingView;
                      }
                      return (
                        <Fragment key={c.condition.id}>
                          <button
                            className={twMerge(
                              isDisabled ? 'bg-gray-100 opacity-50' : 'hover:bg-gray-100  bg-white-100',
                              `group flex items-center w-full py-3 pl-3 pr-6 `
                            )}
                            disabled={isLoading || isDisabled}
                            onClick={() => {
                              onConditionSelected(c.condition);
                              close();
                            }}
                          >
                            <c.icon className={'mr-3 size-5 text-quaternary-400'} />
                            <span className={twMerge(isDisabled ? '' : '', `text-primary-900 truncate `)}>{c.name}</span>
                          </button>
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
